// BLE transport — scan, connect, write, listen.
//
// Port of the Flutter ble_controller.dart, over @capacitor-community/bluetooth-le.
// Native only; on web every call is a no-op that reports "unsupported" rather
// than throwing, so the desktop build never touches it.
//
// This is what makes Wi-Fi onboarding possible in the React app at all. A
// WebView cannot join a device's setup AP — but it CAN speak BLE through this
// plugin, and the Smartify firmware provisions over BLE, so the whole flow ports
// cleanly.
//
// TRANSPORT ONLY. Authentication, the handshake and the session live in
// bleSession.js; anything that sends a COMMAND goes through there, because a
// device rejects everything until authenticated.

import { BleClient, numbersToDataView, ScanMode } from '@capacitor-community/bluetooth-le';
import {
  BLE_NAME_PREFIX, SERVICE_UUID, CHAR_UUID, WRITE_WITHOUT_RESPONSE,
  SCAN_TIMEOUT_MS, REQUESTED_MTU, decodeNotification, encode, parseServiceData,
} from './bleConfig';
import { isNativePlatform } from '../platform';

// BLE IS MOBILE ONLY. Desktop is a browser with no BLE plugin, and every entry
// point here is gated on this rather than on a try/catch, so the desktop bundle
// can import this module without ever reaching the native layer.
export const bleSupported = () => isNativePlatform();

// ── GATT operations are SERIALISED ─────────────────────────────────────────
//
// One outstanding write/read at a time, per the spec's platform notes:
// "Concurrent operations fail silently on many devices." Silently is the
// problem — an overlapping write is not an error you can catch, it is a command
// that never happened, which during a handshake means an auth that never
// completes for no visible reason.
//
// A single chain is enough: BLE traffic here is a handshake followed by
// occasional commands, never throughput.
let gattChain = Promise.resolve();

function serialise(fn) {
  const run = gattChain.then(fn, fn);
  // Keep the chain alive after a rejection, but let the caller still see it.
  gattChain = run.then(() => undefined, () => undefined);
  return run;
}

let initialised = false;

export async function initBle() {
  if (!bleSupported()) throw new Error('Bluetooth is only available in the app');
  if (initialised) return;
  await BleClient.initialize({ androidNeverForLocation: true });
  initialised = true;
}

// Scan for Smartify devices for SCAN_TIMEOUT_MS, calling onDevice as each is
// seen. Resolves when the scan stops.
export async function scanForDevices(onDevice) {
  await initBle();
  const seen = new Set();

  await BleClient.requestLEScan({
    // Filter on the SERVICE UUID, which is in the advertisement proper and is
    // the reliable filter on both platforms. The name is only in the scan
    // response, so filtering by name alone loses devices whose response has not
    // arrived yet.
    services: [SERVICE_UUID],
    allowDuplicates: false,
    // ⚠️ LOW_LATENCY IS ACTIVE SCANNING. 31 bytes cannot hold both the service
    // data and the name, so the firmware puts the name in the SCAN RESPONSE —
    // which only exists if the scan actively asks for it. A passive scan sees
    // the service UUID and no name, and a name-based filter then matches
    // nothing, indistinguishable from no devices present.
    scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
  }, (result) => {
    const name = result.localName || result.device?.name || '';
    if (!name.startsWith(BLE_NAME_PREFIX)) return;
    if (seen.has(result.device.deviceId)) return;
    seen.add(result.device.deviceId);
    // The advertised state/owner_tag, so the list can say "new" or "mine"
    // without connecting to anything.
    const raw = result.serviceData && result.serviceData[SERVICE_UUID];
    onDevice({
      deviceId: result.device.deviceId,
      name,
      uniqueNumber: name.slice(BLE_NAME_PREFIX.length) || null,
      rssi: result.rssi,
      service: raw ? parseServiceData(raw) : null,
    });
  });

  await new Promise((r) => { setTimeout(r, SCAN_TIMEOUT_MS); });
  try { await BleClient.stopLEScan(); } catch (e) { /* already stopped */ }
}

export async function stopScan() {
  try { await BleClient.stopLEScan(); } catch (e) { /* ignore */ }
}

export async function connect(deviceId, onDisconnect) {
  await initBle();
  await BleClient.connect(deviceId, onDisconnect);
  // Negotiate the MTU immediately after connecting and BEFORE any write.
  // Skipping this truncates AUTH (37 bytes) and WIFI (up to 101) against a
  // 20-byte default payload, with no error — see REQUESTED_MTU.
  //
  // Best-effort: iOS negotiates on its own and rejects the call, and some
  // Android stacks refuse. A refusal is not fatal on its own — the firmware
  // reassembles chunked writes — so it is logged rather than thrown.
  try {
    await BleClient.requestMtu(deviceId, REQUESTED_MTU);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[BLE] MTU negotiation refused; long writes may be chunked');
  }
}

export async function disconnect(deviceId) {
  try { await BleClient.disconnect(deviceId); } catch (e) { /* ignore */ }
}

// Subscribe to the notify characteristic. Returns an unsubscribe function.
export async function listen(deviceId, onText) {
  await BleClient.startNotifications(deviceId, SERVICE_UUID, CHAR_UUID, (value) => {
    const text = decodeNotification(value);
    if (text) onText(text);
  });
  return async () => {
    try {
      await BleClient.stopNotifications(deviceId, SERVICE_UUID, CHAR_UUID);
    } catch (e) { /* ignore */ }
  };
}

export async function write(deviceId, text) {
  const data = encode(text);
  return serialise(async () => {
    if (WRITE_WITHOUT_RESPONSE) {
      try {
        await BleClient.writeWithoutResponse(deviceId, SERVICE_UUID, CHAR_UUID, data);
        return;
      } catch (e) {
        // Fall through — some stacks reject write-without-response mid-session.
      }
    }
    await BleClient.write(deviceId, SERVICE_UUID, CHAR_UUID, data);
  });
}

// A GATT read of the characteristic returns the current status string (§4).
export async function readStatus(deviceId) {
  return serialise(async () => {
    const value = await BleClient.read(deviceId, SERVICE_UUID, CHAR_UUID);
    return decodeNotification(value);
  });
}

// Exposed for callers that already hold raw bytes.
export { numbersToDataView };
